import { Controller } from "@hotwired/stimulus"

/**
 * Billing Address Manager Controller
 *
 * Manages the billing address selector with inline add/edit functionality.
 * Coordinates with slide-drawer for form display and handles AJAX form submission.
 */
export default class extends Controller {
  static targets = ["addressList", "drawer", "drawerContent", "drawerTitle", "card"]
  static values = {
    drawerId: String,
    cardColor: { type: String, default: "blue" },
    inlineNewUrl: String
  }

  connect() {
    // Listen for address saved events
    this.element.addEventListener("billing-address:saved", this.handleAddressSaved.bind(this))

    // Initialize visual state for pre-selected radio
    this.initializeVisualState()
  }

  // Set initial visual state for any pre-checked radio
  initializeVisualState() {
    const checkedRadio = this.element.querySelector("input[type='radio']:checked")
    if (checkedRadio) {
      const card = checkedRadio.closest("label")
      this.updateCardVisualState(card, true)
    }
  }

  disconnect() {
    this.element.removeEventListener("billing-address:saved", this.handleAddressSaved.bind(this))
  }

  // Stimulus action: handles card selection when clicking on the card
  // This ensures radio selection works even when buttons inside labels interfere
  selectCard(event) {
    // Don't select if clicking on the edit button
    if (event.target.closest("button[data-action*='openEditForm']")) {
      return
    }

    // Prevent label's native toggle behavior to avoid double-toggle issues
    event.preventDefault()

    // Find the radio input in this card and select it
    const card = event.currentTarget
    const radio = card.querySelector("input[type='radio']")

    if (radio) {
      // Uncheck all other radios in the same group and update their visual state
      const radioName = radio.name
      const allCards = this.element.querySelectorAll(`input[name="${radioName}"]`)
      allCards.forEach(r => {
        r.checked = false
        this.updateCardVisualState(r.closest("label"), false)
      })

      // Check this radio and update visual state
      radio.checked = true
      this.updateCardVisualState(card, true)
      radio.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  // Update the visual state of a card (border, background, indicator)
  updateCardVisualState(card, isChecked) {
    if (!card) return

    const color = this.cardColorValue
    const cardDiv = card.querySelector("div.relative")
    const indicator = card.querySelector(".rounded-full.border-2")
    const checkIcon = indicator?.querySelector("svg")

    if (cardDiv) {
      if (isChecked) {
        // Add checked styles
        cardDiv.classList.remove("border-gray-200", "bg-white")
        cardDiv.classList.add(`border-${color}-500`, `bg-${color}-50`)
      } else {
        // Remove checked styles
        cardDiv.classList.remove(`border-${color}-500`, `bg-${color}-50`)
        cardDiv.classList.add("border-gray-200", "bg-white")
      }
    }

    if (indicator) {
      if (isChecked) {
        indicator.classList.remove("border-gray-300")
        indicator.classList.add(`border-${color}-500`, `bg-${color}-500`)
      } else {
        indicator.classList.remove(`border-${color}-500`, `bg-${color}-500`)
        indicator.classList.add("border-gray-300")
      }
    }

    if (checkIcon) {
      if (isChecked) {
        checkIcon.classList.remove("opacity-0")
        checkIcon.classList.add("opacity-100")
      } else {
        checkIcon.classList.remove("opacity-100")
        checkIcon.classList.add("opacity-0")
      }
    }
  }

  async openNewForm(event) {
    event.preventDefault()
    event.stopPropagation()

    // Update drawer title
    if (this.hasDrawerTitleTarget) {
      this.drawerTitleTarget.textContent = "Novo Endereço"
    }

    // Show loading state
    this.showLoading()

    // Open drawer
    this.openDrawer()

    // Fetch form
    try {
      const url = `${this.inlineNewUrlValue}?card_color=${this.cardColorValue}`
      const response = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const html = await response.text()
        this.drawerContentTarget.innerHTML = html
      } else {
        this.showError("Erro ao carregar formulário")
      }
    } catch (error) {
      console.error("Error fetching form:", error)
      this.showError("Erro de conexão")
    }
  }

  async openEditForm(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const editUrl = button.dataset.editUrl

    // Update drawer title
    if (this.hasDrawerTitleTarget) {
      this.drawerTitleTarget.textContent = "Editar Endereço"
    }

    // Show loading state
    this.showLoading()

    // Open drawer
    this.openDrawer()

    // Fetch form
    try {
      const url = `${editUrl}?card_color=${this.cardColorValue}`
      const response = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const html = await response.text()
        this.drawerContentTarget.innerHTML = html
      } else {
        this.showError("Erro ao carregar formulário")
      }
    } catch (error) {
      console.error("Error fetching form:", error)
      this.showError("Erro de conexão")
    }
  }

  openDrawer() {
    const drawer = this.hasDrawerTarget ? this.drawerTarget : document.getElementById(this.drawerIdValue)
    if (drawer) {
      const drawerController = this.application.getControllerForElementAndIdentifier(drawer, "slide-drawer")
      if (drawerController) {
        drawerController.open()
      }
    }
  }

  closeDrawer() {
    const drawer = this.hasDrawerTarget ? this.drawerTarget : document.getElementById(this.drawerIdValue)
    if (drawer) {
      const drawerController = this.application.getControllerForElementAndIdentifier(drawer, "slide-drawer")
      if (drawerController) {
        drawerController.close()
      }
    }
  }

  showLoading() {
    if (this.hasDrawerContentTarget) {
      this.drawerContentTarget.innerHTML = `
        <div class="flex items-center justify-center py-12">
          <svg class="animate-spin h-8 w-8 text-${this.cardColorValue}-600" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      `
    }
  }

  showError(message) {
    if (this.hasDrawerContentTarget) {
      this.drawerContentTarget.innerHTML = `
        <div class="text-center py-8">
          <svg class="w-12 h-12 mx-auto text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
          <p class="text-gray-600">${message}</p>
          <button type="button"
                  data-action="click->slide-drawer#close"
                  class="mt-4 px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
            Fechar
          </button>
        </div>
      `
    }
  }

  handleAddressSaved(event) {
    const { address, isNew } = event.detail

    if (isNew) {
      // Add new address card to the list
      this.addAddressCard(address)
    } else {
      // Update existing address card
      this.updateAddressCard(address)
    }

    // Close drawer
    this.closeDrawer()

    // Select the new/updated address
    setTimeout(() => {
      const radio = this.element.querySelector(`input[type="radio"][value="${address.id}"]`)
      if (radio) {
        radio.checked = true
        radio.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }, 100)
  }

  addAddressCard(address) {
    const cardHtml = this.buildAddressCardHtml(address)

    // Check if we need to remove the "no addresses" placeholder
    const placeholder = this.element.querySelector('[class*="border-dashed"]')
    if (placeholder) {
      // Create address list container to replace the placeholder
      const listContainer = document.createElement("div")
      listContainer.className = "space-y-2"
      listContainer.setAttribute("data-billing-address-manager-target", "addressList")
      listContainer.innerHTML = cardHtml
      placeholder.replaceWith(listContainer)
    } else if (this.hasAddressListTarget) {
      // Append to existing list
      this.addressListTarget.insertAdjacentHTML("beforeend", cardHtml)
    }
  }

  updateAddressCard(address) {
    const existingCard = this.element.querySelector(`[data-address-id="${address.id}"]`)
    if (existingCard) {
      const newCard = document.createElement("div")
      newCard.innerHTML = this.buildAddressCardHtml(address)
      const cardElement = newCard.firstElementChild
      existingCard.replaceWith(cardElement)
    }
  }

  buildAddressCardHtml(address) {
    const color = this.cardColorValue
    const holderBadge = address.holder_name ? `
      <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
        <svg class="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
        </svg>
        ${address.holder_name}
      </span>
    ` : ""

    const complement = address.complement ? ` - ${address.complement}` : ""

    return `
      <label class="block cursor-pointer group"
             data-billing-address-manager-target="card"
             data-address-id="${address.id}"
             data-action="click->billing-address-manager#selectCard">
        <input type="radio"
               name="${this.getFieldName()}"
               value="${address.id}"
               class="sr-only peer">
        <div class="relative p-3 rounded-lg border-2 transition-all duration-150
                    border-gray-200 bg-white
                    peer-checked:border-${color}-500 peer-checked:bg-${color}-50
                    hover:border-gray-300 peer-checked:hover:border-${color}-500
                    group-hover:shadow-sm">
          <!-- Action buttons -->
          <div class="absolute top-3 right-3 flex items-center gap-1">
            <!-- Edit button -->
            <button type="button"
                    data-action="click->billing-address-manager#openEditForm"
                    data-address-id="${address.id}"
                    data-edit-url="/enderecos-cobranca/${address.id}/inline_edit"
                    class="p-1 text-gray-400 hover:text-${color}-600 hover:bg-${color}-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                    title="Editar">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </button>
            <!-- Selection indicator -->
            <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
                        border-gray-300
                        group-[:has(input:checked)]:border-${color}-500 group-[:has(input:checked)]:bg-${color}-500">
              <svg class="w-3 h-3 text-white opacity-0 group-[:has(input:checked)]:opacity-100" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
              </svg>
            </div>
          </div>

          <!-- Card content -->
          <div class="pr-16">
            <div class="flex items-center gap-2 mb-1">
              <span class="font-medium text-gray-900 text-sm">${address.label}</span>
              ${holderBadge}
            </div>
            <p class="text-sm text-gray-600">
              ${address.address}, ${address.address_number}${complement}
            </p>
            <p class="text-xs text-gray-500">
              ${address.neighborhood} • ${address.city}/${address.state}
              ${address.formatted_zipcode ? `• CEP ${address.formatted_zipcode}` : ""}
            </p>
          </div>
        </div>
      </label>
    `
  }

  getFieldName() {
    // Find the first radio input to get the field name
    const radio = this.element.querySelector('input[type="radio"]')
    return radio ? radio.name : "billing_address_id"
  }
}
